import React, { useCallback, useEffect, useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { fetchJSON } from '../../utils/api';
import { formatDate } from '../../utils/dateFormat';
import SessionMaterials from './SessionMaterials';
import { subsectionTitle, subsectionTitleBar } from '../workflow/constants';

/**
 * Training Records → Sessions → Materials: L&D's door for a session's course
 * materials and recording (docs/modules/ld.md §3.6). Trainers manage the
 * same files from their session page in My Records; L&D (admin included)
 * manages them HERE — the My Records page is read-only for them, so the two
 * entrances never blur.
 *
 * Both lists are loaded on open and refreshed after every change; the modal
 * itself has no save button — each upload, link and removal is immediate.
 */
const SessionFilesModal = ({ session, onClose }) => {
  const [files, setFiles] = useState(null);
  const [recordings, setRecordings] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const [m, r] = await Promise.all([
      fetchJSON(`/api/ld/sessions/${session.id}/files?kind=material`),
      fetchJSON(`/api/ld/sessions/${session.id}/files?kind=recording`),
    ]);
    setFiles(m.data || []);
    setRecordings(r.data || []);
  }, [session.id]);

  useEffect(() => {
    let cancelled = false;
    load().catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [load]);

  const Section = ({ title, count, children }) => (
    <section className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
        <h4 className={subsectionTitle}>
          <span className={subsectionTitleBar} aria-hidden="true" />
          {title}
        </h4>
        {count != null && <span className="text-xs text-slate-500">({count})</span>}
      </div>
      {children}
    </section>
  );

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-slate-900 truncate">Materials and Recording</h3>
            <p className="text-sm text-slate-500 truncate">
              {session.courseTitle || session.sessionCode} · {formatDate(session.date)}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />{error}
            </div>
          )}
          {!error && files === null && <p className="text-sm text-slate-500">Loading…</p>}
          {files !== null && (
            <>
              <Section title="Course Materials" count={files.length}>
                <SessionMaterials sessionId={session.id} kind="material" files={files} canManage onChanged={setFiles} />
              </Section>
              <Section title="Recording" count={(recordings || []).length}>
                <SessionMaterials sessionId={session.id} kind="recording" files={recordings || []} canManage onChanged={setRecordings} />
              </Section>
              <p className="text-xs text-slate-500">
                Participants download these from their session page in My Records; the session’s trainers can add to them there too.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionFilesModal;
